import {
  appointmentModeValueMapping,
  CounselingTypeEnum,
} from 'app/Bookings/constants'

import {
  dateFormats,
  DayOfWeek,
  minimalTimezoneSet,
  sessionStorageKeys,
  supportDashboardToProviderSearchKeys,
  timeOfDayDescription,
} from 'constants/common'
import { profileDayOfWeekFilterOptions } from 'constants/profileDayOfWeekFilterOptions'
import {
  providerFilterKeyMapping,
  ProviderFilterTypes,
} from 'dataSource/ProviderSearch'
import { getDay, set } from 'date-fns'
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz'
import addDays from 'date-fns/addDays'
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays'
import addHours from 'date-fns/addHours'
import differenceInHours from 'date-fns/differenceInHours'
import format from 'date-fns/format'
import startOfDay from 'date-fns/startOfDay'
import subDays from 'date-fns/subDays'
import subMinutes from 'date-fns/subMinutes'
import _groupBy from 'lodash/groupBy'
import _pick from 'lodash/pick'
import _pickBy from 'lodash/pickBy'
import _sortBy from 'lodash/sortBy'
import { NextRouter } from 'next/router'
import qs from 'qs'
import { ParsedUrlQuery } from 'querystring'
import { getCurrentZonnedTime, PG_DOW_TO_DAY_OF_WEEK } from 'utils/common'
import {
  callCenterSchedule,
  EXTENDED_BOOKING_LAST_DAY_OFFSET_FROM_ANCHOR,
  EXTENDED_BOOKING_SPAN_DAYS,
  FILTER_KEY_MAPPING,
  NON_BINARY_GENDER_DISPLAY_VALUE,
  NON_BINARY_GENDER_VALUE,
  OTHER_GENDER_VALUE,
  REQUIRED_GENDER_VALUE_ORDER,
  RETURN_FILTER_KEY_MAPPING,
  SLOT_CALENDAR_LAST_DAY_OFFSET_FROM_ANCHOR,
  SLOT_CALENDAR_SPAN_DAYS,
} from './constants'
import { counselingTypeSortOrder } from './ProviderFilter/CounselingFilter/constants'
import { INTAKE_BOOKING_WINDOW_HOURS } from 'constants/common'
import { stateAbbreviations } from 'app/SeFriendly/validateUsaState'

/**
 * Calculates and returns the max of dates from slots
 * If the slots is less then 3 then it will return 3
 */

const getMaxLengthFromSlots = (slots: DateSlot, visibleDates: Date[]) => {
  let maxLength = 0

  const slotKeys = visibleDates.map((date) =>
    format(date, dateFormats.YEAR_MONTH_DAY_FORMAT)
  )

  for (const slotKey of slotKeys) {
    const slot = slots?.[slotKey]
    maxLength = slot && slot.length > maxLength ? slot.length : maxLength
  }

  return maxLength > 3 ? maxLength : 3
}

/**
 * Returns a array of dates that is generated from the start date till the number limit
 */
export const generateDates = (
  startSlotDate: Date,
  slotListLimit: number
): Date[] => {
  let date: Date = new Date(startSlotDate)
  const endSlotDate: Date = addDays(startSlotDate, slotListLimit)
  const dates: Date[] = []

  while (date < endSlotDate) {
    dates.push(date)
    date = addDays(date, 1)
  }

  return dates
}

/**
 * Computes the paginator window start so a focus date near the booking horizon
 * appears in the correct column instead of anchoring at the first cell.
 *
 * Examples with slotListLimit = 5 and maxSlotDate = Sep 15:
 * - Sep 15 → Sep 11 (offset slotListLimit - 1)
 * - Sep 14 → Sep 11 (offset slotListLimit - 2)
 * - Sep 10 → Sep 10 (offset 0)
 */
export const getSlotWindowStart = (
  focusDate: Date,
  maxSlotDate: Date,
  slotListLimit: number,
  minSlotDate: Date
): Date => {
  const focusDay = startOfDay(focusDate)
  const maxDay = startOfDay(maxSlotDate)
  const minDay = startOfDay(minSlotDate)
  const daysFromMax = Math.max(0, differenceInCalendarDays(maxDay, focusDay))
  const maxDaysBeforeFocus = slotListLimit - 1
  const daysBeforeFocus = Math.min(
    maxDaysBeforeFocus,
    Math.max(0, maxDaysBeforeFocus - daysFromMax)
  )
  const windowStart = subDays(focusDay, daysBeforeFocus)

  return windowStart < minDay ? minDay : windowStart
}

// Last bookable day for extended booking: today + 10 weeks, in the given timezone.
export const getExtendedBookingMaxSlotDate = (timeZone: string): Date =>
  addDays(
    getCurrentZonnedTime(timeZone),
    EXTENDED_BOOKING_LAST_DAY_OFFSET_FROM_ANCHOR
  )

// Shrinks the slot window so a query/display never runs past the 10-week cap.
export const getCappedExtendedBookingSlotListLimit = (
  startSlotDate: Date,
  slotListLimit: number,
  timeZone: string
): number => {
  const maxSlotDate = getExtendedBookingMaxSlotDate(timeZone)
  const daysUntilMax = differenceInCalendarDays(maxSlotDate, startSlotDate) + 1

  return Math.max(0, Math.min(slotListLimit, daysUntilMax))
}

const zonedCalendarDayString = (date: Date, timeZone: string): string =>
  format(utcToZonedTime(date, timeZone), dateFormats.YEAR_MONTH_DAY_FORMAT)

const maskDatesOutsideSlotCalendarWindow = (
  dates: Date[],
  timeZone: string,
  lastDayOffsetFromAnchor: number = SLOT_CALENDAR_LAST_DAY_OFFSET_FROM_ANCHOR
): (Date | null)[] => {
  const nowAnchored = getCurrentZonnedTime(timeZone)
  const todayStr = zonedCalendarDayString(nowAnchored, timeZone)
  const lastBookableStr = zonedCalendarDayString(
    addDays(nowAnchored, lastDayOffsetFromAnchor),
    timeZone
  )

  return dates.map((date) => {
    const candidateStr = zonedCalendarDayString(date, timeZone)

    if (candidateStr < todayStr || candidateStr > lastBookableStr) {
      return null
    }

    return date
  })
}

export const generateDatesWithDayOfWeekFilter = (
  startSlotDate: Date,
  slotListLimit: number,
  dayOfWeek: DayOfWeek[] | undefined,
  timeZone: string | undefined,
  extendedBookingEnabled = false
): (Date | null)[] => {
  if (!dayOfWeek?.length) {
    return generateDates(startSlotDate, slotListLimit)
  }

  if (!timeZone) {
    return generateDates(startSlotDate, slotListLimit)
  }

  // A single selected day (e.g. Saturday-only) needs a much wider lookahead
  // than a non-filtered request to find `slotListLimit` matching dates, so
  // the candidate window follows the same extended-booking horizon used
  // elsewhere instead of always searching the short 21-day window.
  const spanDays = extendedBookingEnabled
    ? EXTENDED_BOOKING_SPAN_DAYS
    : SLOT_CALENDAR_SPAN_DAYS
  const lastDayOffsetFromAnchor = extendedBookingEnabled
    ? EXTENDED_BOOKING_LAST_DAY_OFFSET_FROM_ANCHOR
    : SLOT_CALENDAR_LAST_DAY_OFFSET_FROM_ANCHOR

  const candidates = generateDates(startSlotDate, spanDays)
  const matching = candidates.filter((date) => {
    const dow = PG_DOW_TO_DAY_OF_WEEK[getDay(date)]
    return dayOfWeek.includes(dow)
  })

  const sliced = matching.slice(0, slotListLimit)
  const withMasking = maskDatesOutsideSlotCalendarWindow(
    sliced,
    timeZone,
    lastDayOffsetFromAnchor
  )

  while (withMasking.length < slotListLimit) {
    withMasking.push(null)
  }

  return withMasking
}

/**
 * Generates a new slot mapping according to the dates provided
 * it would fill the missing slots with null till the max length
 */
export const generateDatedSlots = (
  dates: Date[],
  dateSlots: DateSlot,
  validateSlotCondition = (slot: ProviderSlot) => slot
): DateSlot => {
  const generateDatedSlots: DateSlot = {}

  const maxLength = getMaxLengthFromSlots(dateSlots, dates)

  dates.map((date) => {
    // The dates being passed to this function already has the timezone calculation, so not required to parse and calculate timezone
    const formattedDate = format(date, dateFormats.YEAR_MONTH_DAY_FORMAT)
    const slots = !!dateSlots ? dateSlots[formattedDate] : []
    const returnedSlots = []

    for (let i = 0; i < maxLength; i++) {
      if (slots?.[i]) {
        let updatedSlot = slots[i]

        if (!!validateSlotCondition) {
          updatedSlot = validateSlotCondition(slots[i])
        }

        if (updatedSlot) {
          returnedSlots.push(updatedSlot)
        }

        continue
      }

      returnedSlots.push(null)
    }

    generateDatedSlots[formattedDate] = returnedSlots.sort((slotA, slotB) => {
      if (slotA && slotB) {
        return new Date(slotA).getTime() - new Date(slotB).getTime()
      } else if (slotA) {
        return -1
      }

      return 0
    })
  })

  return generateDatedSlots
}

export const isSlotOutsideBookingWindow = (
  slotStartTime: string | number,
  bookingWindowHours: number
): boolean =>
  !slotStartTime ||
  differenceInHours(new Date(slotStartTime), new Date()) >= bookingWindowHours

const hardCodedUpdatedFilter = (filter: ProviderFilter): ProviderFilter => {
  let updatedFilter = filter

  // Add any list of hard coded filter condition here
  if (filter.type === ProviderFilterTypes.GENDER) {
    if (filter.value === OTHER_GENDER_VALUE) {
      updatedFilter = {
        ...filter,
        displayValue: 'Other',
      }
    } else if (filter.value === NON_BINARY_GENDER_VALUE) {
      updatedFilter = {
        ...filter,
        displayValue: NON_BINARY_GENDER_DISPLAY_VALUE,
      }
    }
  } else if (filter.type === ProviderFilterTypes.CLIENT_AGE_GROUP) {
    // AJ booking fixes, remove age description
    updatedFilter = {
      ...filter,
      value: `${filter.value} years old`,
      description: '',
    }
  } else if (filter.type === ProviderFilterTypes.TIME_OF_DAY) {
    updatedFilter = {
      ...filter,
      id: filter.value,
      value: filter.description,
      description: timeOfDayDescription[filter.value],
    }
  } else if (filter.type === ProviderFilterTypes.DAY_OF_WEEK) {
    updatedFilter = {
      ...filter,
      id: filter.value,
      value: filter.description,
      description: '',
    }
  }

  const { type } = updatedFilter
  // additional is group that is not available in ProviderFilterTypes
  const filterType = [...Object.values(ProviderFilterTypes)].includes(
    type as ProviderFilterTypes
  )
    ? type
    : 'additional'
  return {
    ...updatedFilter,
    type: filterType,
  }
}

/**
 * Returns a grouped array of filter type from a single filter array
 */
export const groupFilters = (
  filters: ProviderFilter[]
): GroupedProviderFilter => {
  const filterGroup: GroupedProviderFilter = _groupBy(
    filters.map(hardCodedUpdatedFilter),
    (filter) => filter.type
  )

  const sortedFilters: GroupedProviderFilter = {}

  // Sorting the grouped values if its required in some order
  for (const [filterType, filterValues] of Object.entries(filterGroup)) {
    let sortedValues

    if (filterType === ProviderFilterTypes.CLIENT_AGE_GROUP) {
      sortedValues = _sortBy(filterValues, [(a) => parseInt(a.value)])
    } else if (filterType === ProviderFilterTypes.GENDER) {
      sortedValues = _sortBy(filterValues, (item) => {
        const orderIndex = REQUIRED_GENDER_VALUE_ORDER.indexOf(item.value)
        return orderIndex === -1
          ? REQUIRED_GENDER_VALUE_ORDER.length
          : orderIndex
      })
    } else if (filterType === ProviderFilterTypes.TIME_OF_DAY) {
      const order = ['early_morning', 'morning', 'afternoon', 'evening']
      sortedValues = _sortBy(filterValues, (item) => order.indexOf(item.id))
    } else if (filterType === ProviderFilterTypes.DAY_OF_WEEK) {
      const order = profileDayOfWeekFilterOptions.map((o) => o.id)
      sortedValues = _sortBy(filterValues, (item) => order.indexOf(item.id))
    } else {
      sortedValues = _sortBy(filterValues, ['value'])
    }

    sortedFilters[filterType] = sortedValues
  }

  return sortedFilters
}

export const getRequiredGenderFilters = (
  filters: ProviderFilter[] = []
): ProviderFilter[] =>
  filters.filter((filter) => REQUIRED_GENDER_VALUE_ORDER.includes(filter.value))

/**
 * Marks which required genders have providers in the state. Pass `null` when
 * state availability is unknown (no state, or the request failed) to leave
 * every gender enabled.
 */
export const mergeGenderFiltersWithStateAvailability = (
  allFilters: ProviderFilter[] = [],
  stateScopedFilters: ProviderFilter[] | null
): ProviderFilter[] => {
  const requiredFilters = getRequiredGenderFilters(allFilters)

  if (!stateScopedFilters) {
    return requiredFilters
  }

  const availableIds = new Set(stateScopedFilters.map((filter) => filter.id))

  return requiredFilters.map((filter) => ({
    ...filter,
    isAvailable: availableIds.has(filter.id),
  }))
}

/**
 * Adds the selected values the state doesn't offer, taken from the full filter
 * list and marked unavailable, so a selection kept from another state shows
 * greyed out until it is deselected. Values keep the full list's order.
 * Without the full list (e.g. SE-friendly pages) the state's list is returned.
 */
export const withUnavailableSelectedFilters = (
  stateFilters: ProviderFilter[] = [],
  allFilters: ProviderFilter[] | null | undefined,
  selectedIds: string[] = []
): ProviderFilter[] => {
  const stateFiltersById = new Map(
    stateFilters.map((filter) => [filter.id, filter])
  )
  const hasUnavailableSelection = selectedIds.some(
    (id) =>
      !stateFiltersById.has(id) &&
      !!allFilters?.some((filter) => filter.id === id)
  )

  if (!hasUnavailableSelection) {
    return stateFilters
  }

  return allFilters.reduce<ProviderFilter[]>((filters, filter) => {
    if (stateFiltersById.has(filter.id)) {
      return [...filters, stateFiltersById.get(filter.id)]
    }

    if (selectedIds.includes(filter.id)) {
      return [...filters, { ...filter, isAvailable: false }]
    }

    return filters
  }, [])
}

export interface ParsedFilterType {
  availabilityType?: string[]
  areaOfConcern?: string[]
  clientAgeGroup?: string[]
  gender?: string[]
  counselingApproach?: string[]
  language?: string[]
  timeOfDay?: string[]
  dayOfWeek?: string[]
}

export const getFilterLookup = (
  filters: ProviderFilter[]
): ProviderFilterLookup =>
  _groupBy<ProviderFilter>(filters, (filter) => filter.id)

/**
 * Format selected grouped filter as required by API
 */
export const formatSelectedFilters = (
  groupedFilters: GroupedProviderFilter,
  filterKeys = RETURN_FILTER_KEY_MAPPING
): ParsedFilterType => {
  const parsedFilter = {}

  Object.keys(groupedFilters).map((groupedFilterKey) => {
    parsedFilter[filterKeys[groupedFilterKey]] = groupedFilters[
      groupedFilterKey
    ].map((filter) => filter?.id)
  })

  return parsedFilter
}

/**
 * Format selected grouped filter as required by API (For Split new search only)
 */
export const formatSelectedFiltersWithCounselingType = (
  groupedFilters: GroupedProviderFilter
): ParsedFilterType => {
  const returnFilterKeyMappingWithCounselingType = {
    ...RETURN_FILTER_KEY_MAPPING,
    counseling_type: 'counselingType',
  }

  return formatSelectedFilters(
    groupedFilters,
    returnFilterKeyMappingWithCounselingType
  )
}

// A selected gender the state doesn't offer is still searched for, so the
// waterfall drops it and lists it under "Not filtered by" like other filters.
export const getGenderValuesFromIds = (
  genderIds: string[],
  groupedGender: ProviderFilter[]
): string[] | null => {
  if (genderIds) {
    return groupedGender
      .filter((filter) => genderIds.includes(filter.id))
      .map((filter) => filter.value)
  }

  return null
}

/**
 * get selected GroupedProvider based on query params
 */
export const getSelectedFiltersFromQueryParams = (
  groupedFilters: GroupedProviderFilter,
  query: NodeJS.Dict<string | string[]>,
  filterKeys = RETURN_FILTER_KEY_MAPPING,
  // Full filter list, used for selected values the state doesn't offer
  allGroupedFilters: GroupedProviderFilter | null = null
): GroupedProviderFilter => {
  const selectedFilter: GroupedProviderFilter = {}

  const findFilter = (
    filterType: string,
    filterId: string
  ): ProviderFilter | undefined =>
    groupedFilters[filterType]?.find(
      (filter: ProviderFilter) => filter.id === filterId
    ) ??
    allGroupedFilters?.[filterType]?.find(
      (filter: ProviderFilter) => filter.id === filterId
    )

  // finds the id from groupedFilters and creates a object for selected filters
  Object.keys(query).map((queryKey) => {
    const groupedFilterKey = queryKey.replace('[]', '')

    if (
      (Object.values(filterKeys) as readonly string[]).includes(
        groupedFilterKey
      )
    ) {
      const filterType = FILTER_KEY_MAPPING[groupedFilterKey]

      if (Array.isArray(query[queryKey])) {
        const queryValue = query[queryKey] as string[]
        selectedFilter[filterType] = queryValue
          .map((queryFilterId) => findFilter(filterType, queryFilterId))
          .filter((filter): filter is ProviderFilter => !!filter)
      } else {
        // When there is single filter param it parses as a string not as a array
        const filter = findFilter(filterType, query[queryKey] as string)
        selectedFilter[filterType] = filter ? [filter] : []
      }
    }
  })

  return selectedFilter
}

/**
 * get selected GroupedProvider based on query params (For Split new search only)
 */
export const getSelectedFiltersWithCounselingTypeFromQueryParams = (
  groupedFilters: GroupedProviderFilter,
  query: NodeJS.Dict<string | string[]>,
  allGroupedFilters: GroupedProviderFilter | null = null
): GroupedProviderFilter => {
  const returnFilterKeyMappingWithCounselingType = {
    ...RETURN_FILTER_KEY_MAPPING,
    counseling_type: 'counselingType',
  }

  return getSelectedFiltersFromQueryParams(
    groupedFilters,
    query,
    returnFilterKeyMappingWithCounselingType,
    allGroupedFilters
  )
}

export const formatPracticesFromProviders = (
  providers: Provider[] = [],
  initialPractices: PracticeProvider[] = []
): PracticeProvider[] => {
  const practices = initialPractices
  providers.map((provider) => {
    const practice = provider?.practice
    const index = practices.findIndex(
      (existingPractice) => existingPractice.id === practice.id
    )

    if (index !== -1) {
      practices[index].providers.push(provider)
    } else {
      practices.push({
        ...practice,
        providers: [provider],
      })
    }
  })
  return practices
}

//Check if only all Providers Filter exists in the query params
export const isOnlyAllProviderFilter = (queryObj: qs.ParsedQs): boolean => {
  const validKeys = [
    'state',
    'latitude',
    'longitude',
    'location',
    'limit',
    'offset',
    'slotStartDate',
    'slotEndDate',
    'distanceInMiles',
    ...supportDashboardToProviderSearchKeys,
  ]

  const isAllProviderFilter = Object.keys(queryObj).every((queryParam) =>
    validKeys.includes(queryParam)
  )
  return isAllProviderFilter
}

/**
 * @description
 * creates formattedTimeZoneSet which is a list of timezones converted to an option list which could be accepted by the Select Component
 * @param minimalTimezoneSet list of the formatted timezones we support.
 * @returns {TimezoneSelectItem} the formatted list created from {minimalTimezoneSet} and adds the current timezone if the current timezone is not present in minimalTimezoneSet.
 */
export const getFormattedListTimeZones = (): TimezoneSelectItem[] => {
  const currentTimeZone: string = Intl.DateTimeFormat().resolvedOptions()
    .timeZone
  const formattedTimeZoneSet = minimalTimezoneSet.map(
    ({ label, tzCode, tzName, tzAbbr }) => ({
      label,
      value: tzName,
      tzCode,
      tzAbbr,
    })
  )

  if (minimalTimezoneSet.find(({ tzCode }) => tzCode === currentTimeZone)) {
    return formattedTimeZoneSet
  }

  return [
    ...formattedTimeZoneSet,
    {
      label: currentTimeZone,
      value: currentTimeZone,
      tzCode: currentTimeZone,
      tzAbbr: currentTimeZone,
    },
  ]
}

export const matchTimezoneFromList = (
  tzName: string,
  tzCode: string
): TimeZone => {
  let matchingTimezone
  const timeZoneFromList = minimalTimezoneSet.filter(
    (timeZoneItem) => timeZoneItem.tzName === tzName
  )

  if (timeZoneFromList?.length > 1) {
    matchingTimezone = timeZoneFromList.find(
      (timezone) => timezone.tzCode === tzCode
    )
  } else if (timeZoneFromList?.length === 1) {
    matchingTimezone = timeZoneFromList[0]
  }

  return matchingTimezone
}

/**
 *default parameter selects the browser timeZone
 */
export const getTimeZoneFromFormattedList = (
  currentTimeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone,
  tzCode: string
): TimezoneSelectItem => {
  const matchingTimezone = matchTimezoneFromList(currentTimeZone, tzCode)

  if (matchingTimezone) {
    return {
      label: matchingTimezone.label,
      value: matchingTimezone.tzName,
      tzCode: matchingTimezone.tzCode,
      tzAbbr: matchingTimezone.tzAbbr,
    }
  }

  return {
    label: currentTimeZone,
    value: currentTimeZone,
    tzCode: currentTimeZone,
    tzAbbr: currentTimeZone,
  }
}

export const getUserTimeZoneName = (): string => {
  return Intl.DateTimeFormat(undefined, { timeZoneName: 'long' })
    .formatToParts(new Date())
    .find((part) => part.type === 'timeZoneName').value
}

/**
 *  checks if the selected date is within the week of the selected start date
 */
export const isSelectedSlotInRange = (
  selectedSlot: SelectedSlot,
  timeZone: string,
  startSlotDate: Date,
  slotListLimit: number
): boolean => {
  if (selectedSlot.slot?.start) {
    const currentSelectedSlot = utcToZonedTime(
      selectedSlot.slot.start,
      timeZone
    )
    const slotsStartDate = new Date(startSlotDate)
    slotsStartDate.setHours(0, 0, 0, 0)
    const slotsEndDate = new Date(slotsStartDate)
    slotsEndDate.setDate(slotsEndDate.getDate() + slotListLimit)

    const isSlotInRange =
      currentSelectedSlot >= slotsStartDate &&
      currentSelectedSlot <= slotsEndDate
    return isSlotInRange
  }
}

/**
 * Used in List card
 */
export const getBookingQueryObject = (
  queryObject: ParsedUrlQuery,
  slotId: string
): unknown => {
  const { insuranceId, selfPay, userId, patients, leadId } = queryObject

  return {
    ...(insuranceId && { insuranceId }),
    ...(selfPay && { selfPay }),
    ...(userId && { userId }),
    ...(!!patients && { patients }),
    ...(!!leadId && { leadId: leadId }),
    slotId,
  }
}

/**
 * returns the browser timezone date from a zoned timezone date
 */
export const getBrowserTimeFromZonedTime = (
  zonedTime: Date,
  timeZone: string
): Date => {
  const utcTime = zonedTimeToUtc(zonedTime, timeZone)
  const browserTime = utcToZonedTime(
    utcTime,
    Intl.DateTimeFormat().resolvedOptions().timeZone
  )

  return browserTime
}

/**
 * This function is used in
 * - Provider slot hook for slots api
 * - Provider listing page for providers api
 *
 * Calculation logic start date :-
 *    1. if todays date is selected then add bookingWindowHours (2hr) from the current time
 *    2. if any other date is selected then the time of the date will start from the start of the date eg: 21-2-2021T00:00:00 (setting of the time to 00:00:00 is done on the component level not here)
 *
 * Calculation logic end date :-
 *    1. slotListLimit will the number of days that needs to be added to the start date the resulting date will be the end date
 *    2. set the time of the end date to 00:00:00 (start of the day) to fetch exactly till the end date slots
 */
export const getSlotsStartEndDate = (
  startSlotDate: Date,
  slotListLimit: number,
  timeZone: string,
  dayOfWeek?: DayOfWeek[],
  bookingWindowHours: number = INTAKE_BOOKING_WINDOW_HOURS,
  extendedBookingEnabled = false
): SlotsRange => {
  let modifiedStartSlotDate = startSlotDate
  const visibleDates = generateDatesWithDayOfWeekFilter(
    startSlotDate,
    slotListLimit,
    dayOfWeek,
    timeZone,
    extendedBookingEnabled
  )
  const definedVisibleDates = visibleDates.filter((d): d is Date => d !== null)
  const lastVisibleDate =
    definedVisibleDates[definedVisibleDates.length - 1] ?? startSlotDate
  const exactModifiedEndSlotDate = startOfDay(addDays(lastVisibleDate, 1))
  const modifiedEndSlotDate = subMinutes(exactModifiedEndSlotDate, 1)
  const currentZonnedTime = getCurrentZonnedTime(timeZone)

  if (startSlotDate <= addHours(currentZonnedTime, bookingWindowHours)) {
    modifiedStartSlotDate = addHours(currentZonnedTime, bookingWindowHours)
  }

  const browserStartTime = getBrowserTimeFromZonedTime(
    modifiedStartSlotDate,
    timeZone
  )
  const browserEndTime = getBrowserTimeFromZonedTime(
    modifiedEndSlotDate,
    timeZone
  )

  return {
    startDate: browserStartTime,
    endDate: browserEndTime,
  }
}

export const getLabelForFilterItem = (
  filterItem: ProviderFilter,
  showDescriptionInLabel: boolean
): string => {
  let description = filterItem.description || ''

  if (!showDescriptionInLabel) {
    description = ''
  }

  return `${filterItem.displayValue || filterItem.value} ${description}`
}

export const getTextFromHtml = (text: string): string => {
  // This regex targets all the tags in a string and the replace removes those tags.
  const parsedText = text.replace(/<br>(?=(?:\s*<[^>]>)$)|(<br>)|<[^>]*>/gi, '')
  return parsedText.replace(/&nbsp;/g, ' ')
}

export const formatAvailabilityTypeValues = (
  availabilityFilters: ProviderFilter[]
): ProviderFilter[] => {
  return availabilityFilters.map((filter) => ({
    ...filter,
    value: appointmentModeValueMapping[filter.value],
  }))
}

export const mapFilterIDsWithFilterLookup = (
  providerData: ListCardProvider,
  filterPropertyNames: string[],
  filterLookupData: ProviderFilterLookup
): ProviderFilter[] => {
  const filtersData = filterPropertyNames.reduce((allFilters, currPropName) => {
    const filterIds = providerData[currPropName]
    const filterData =
      filterIds?.reduce(
        (filters, id) => [...filters, filterLookupData[id]?.[0] || []],
        []
      ) || []
    return allFilters.concat(filterData)
  }, [])

  return filtersData
}

export const getFilterIdsByValue = (
  groupedFilters: GroupedProviderFilter,
  filterType: string,
  filterValues: string[]
): string[] => {
  const filterIds = []

  groupedFilters[filterType].forEach((filter: ProviderFilter) => {
    if (filterValues.includes(filter.value)) {
      filterIds.push(filter.id)
    }
  })

  return filterIds
}

export const slugifyCity = (city: string): string => {
  return city.split(' ').join('-').toLowerCase()
}

export const getFilterValueFromParsedURL = (
  groupedFilters: GroupedProviderFilter,
  query: ParsedUrlQuery
): { [key: string]: string | string[] | number } | null => {
  const trackerInformation = {}

  Object.keys(providerFilterKeyMapping).forEach((providerFilterKey) => {
    const formattedFilterKey = providerFilterKey.replace('[]', '')
    const queryValue = query[formattedFilterKey]

    if (queryValue) {
      const filterKeyMapped = FILTER_KEY_MAPPING[formattedFilterKey]
      const providerSection = groupedFilters[filterKeyMapped]

      if (providerSection) {
        const queryValueArray = Array.isArray(queryValue)
          ? queryValue
          : [queryValue]
        const selectedValues = providerSection
          .filter((filter) => queryValueArray.includes(filter.id))
          .map((filter) => filter.value)

        trackerInformation[filterKeyMapped] = selectedValues
      }
    }

    return null
  })

  return trackerInformation
}

export const formatSupplyReserveParams = (
  intakeSupplyReserveData: Partial<IntakeSupplyReserveDetails>,
  userType = '',
  counselingType = null
): { [key: string]: string | boolean | number } => {
  const {
    stateCode,
    shouldReserveIntakeSupply,
    shouldReservePsychiatryIntakeSupply,
    partnershipCode,
    businessPartnerTierName,
    businessPartnerTierThreshold,
    totalAvailableIntakes,
  } = intakeSupplyReserveData ?? {}
  let shouldReserveIntakeSupplyFinal = false

  if (counselingType && counselingType.length) {
    if (
      counselingType.includes(CounselingTypeEnum.INDIVIDUAL) ||
      counselingType.includes(CounselingTypeEnum.COUPLES) ||
      counselingType.includes(CounselingTypeEnum.FAMILY)
    ) {
      shouldReserveIntakeSupplyFinal = shouldReserveIntakeSupply
    } else if (counselingType.includes(CounselingTypeEnum.MEDICATION)) {
      shouldReserveIntakeSupplyFinal = shouldReservePsychiatryIntakeSupply
    }
  } else {
    shouldReserveIntakeSupplyFinal =
      shouldReservePsychiatryIntakeSupply && shouldReserveIntakeSupply
  }

  return {
    state_code: stateCode,
    partnership_code: partnershipCode || '',
    business_partner_tier_name: businessPartnerTierName || '',
    business_partner_tier_threshold: businessPartnerTierThreshold || '',
    total_available_intakes: totalAvailableIntakes || '',
    should_reserve_intake_supply: shouldReserveIntakeSupplyFinal,
    user_type: userType,
  }
}

export const hasSecondaryFilters = (
  queryParamObject: Record<string, unknown>,
  SECONDARY_FILTERS: string[]
): boolean => {
  const secondaryFitlers = _pick(queryParamObject, ...SECONDARY_FILTERS)
  const notNullSecondaryFilter = _pickBy(secondaryFitlers, (value) => value)
  const notNullSecondaryFilterKeys = Object.keys(notNullSecondaryFilter)

  return notNullSecondaryFilterKeys.length > 0
}

/**
 * Normalises the `dayOfWeek` URL query param (string or string[]) to a
 * DayOfWeek[] or `undefined`. Used by the DOW empty-state fallback path to
 * detect when a DOW filter is active and to build the "Not filtered by" label.
 */
export const getSelectedDayOfWeek = (
  query: Record<string, unknown>
): DayOfWeek[] | undefined => {
  const raw = query.dayOfWeek as string | string[] | undefined

  if (!raw) {
    return undefined
  }

  const values = Array.isArray(raw) ? raw : [raw]
  const known = new Set<string>(Object.values(DayOfWeek))
  const parsed = values.filter((v): v is DayOfWeek => known.has(v))
  return parsed.length ? parsed : undefined
}

/**
 * Returns true when the conditions for the DOW empty-state fallback are met:
 * - The direct-match fetch returned 0 results (isDirectMatch0WithOnlyPrimaryFilter)
 * - A Day of Week filter is selected in the URL
 * - No secondary filters are active (secondary-only waterfall is NOT yet running)
 *
 * When true, SearchPage should mount ProvidersListWaterfall so the waterfall
 * can drop DOW and surface a "Not filtered by: Monday" bucket.
 */
export const shouldEnableDowOnlyWaterfall = (
  query: Record<string, unknown>,
  secondaryFilters: string[],
  isDirectMatch0WithOnlyPrimaryFilter: boolean
): boolean => {
  return (
    isDirectMatch0WithOnlyPrimaryFilter &&
    !!getSelectedDayOfWeek(query) &&
    !hasSecondaryFilters(query, secondaryFilters)
  )
}

export const getNextEventTimeInLocal = (
  currentETTime: Date,
  currentDayNum: number,
  targetDay: number,
  totalMinutes: number,
  timeZone: string
): string => {
  const eventDate = addDays(currentETTime, (targetDay - currentDayNum + 7) % 7)
  const eventTimeInET = set(eventDate, {
    hours: Math.floor(totalMinutes / 60),
    minutes: totalMinutes % 60,
    seconds: 0,
  })

  const localDate = getBrowserTimeFromZonedTime(eventTimeInET, timeZone)
  return format(localDate, 'h:mma').replace(':00', '') // 7:00AM becomes 7AM
}

export const getCallCenterStatus = (): {
  isCallCenterStatusOpen: boolean
  nextEvent: string
} => {
  const timeZone = 'America/New_York'

  // Get the current time in Eastern Time Zone
  const now = new Date()
  const currentETTime = utcToZonedTime(now, timeZone)
  const currentDayNum = getDay(currentETTime)

  const currentTimeInMinutes =
    currentETTime.getHours() * 60 + currentETTime.getMinutes()

  // Get today's schedule
  const {
    open: todayOpeningTimeInMins,
    close: todayClosingTimeInMins,
  } = callCenterSchedule[currentDayNum]

  if (
    currentTimeInMinutes >= todayOpeningTimeInMins &&
    currentTimeInMinutes < todayClosingTimeInMins
  ) {
    // Call center is open, next event is today's closing time
    const nextEvent = getNextEventTimeInLocal(
      currentETTime,
      currentDayNum,
      currentDayNum,
      todayClosingTimeInMins,
      timeZone
    )

    return {
      isCallCenterStatusOpen: true,
      nextEvent,
    }
  }

  // Call center is closed, check if we're before today's opening or need to move to the next day
  const nextDayNum =
    currentTimeInMinutes < todayOpeningTimeInMins
      ? currentDayNum
      : (currentDayNum + 1) % 7

  const { open: nextDayOpeningInMins } = callCenterSchedule[nextDayNum]
  const nextEvent = getNextEventTimeInLocal(
    currentETTime,
    currentDayNum,
    nextDayNum,
    nextDayOpeningInMins,
    timeZone
  )

  return {
    isCallCenterStatusOpen: false,
    nextEvent,
  }
}

export interface HighlightingInfo {
  selectedSpecialtyIds: string[]
}

/**
 * Gets the selected specialty information from URL query parameters or session storage
 * @param router - Next.js router object
 * @returns Object containing selected specialty IDs
 */
export const getHighlightingInfo = (router: NextRouter): HighlightingInfo => {
  const { query } = router

  let selectedSpecialtyIds: string[] = []

  // Get selected specialties (area of concern) from URL first
  if (query['areaOfConcern[]']) {
    const areaOfConcern = query['areaOfConcern[]']

    if (Array.isArray(areaOfConcern)) {
      selectedSpecialtyIds.push(...areaOfConcern)
    } else {
      selectedSpecialtyIds.push(areaOfConcern)
    }
  }

  // If no specialties in URL, check session storage (for provider profile page)
  if (selectedSpecialtyIds.length === 0 && typeof window !== 'undefined') {
    try {
      const storedSpecialtyIds = sessionStorage.getItem(
        sessionStorageKeys.SELECTED_SPECIALTY_IDS
      )

      if (storedSpecialtyIds) {
        selectedSpecialtyIds = JSON.parse(storedSpecialtyIds)
      }
    } catch (error) {
      // Ignore errors parsing session storage
    }
  }

  return {
    selectedSpecialtyIds,
  }
}

export const getSortedCounselingTypes = (
  filters: ProviderFilter[]
): ProviderFilter[] =>
  [...(filters ?? [])].sort(
    (a, b) =>
      (counselingTypeSortOrder[a.value] ?? 99) -
      (counselingTypeSortOrder[b.value] ?? 99)
  )

// Returns the uppercase abbreviation for a state given as an abbreviation or a full name
const getStateAbbreviation = (state: string): string => {
  const normalizedState = state.trim().toUpperCase()

  if (stateAbbreviations[normalizedState]) {
    return normalizedState
  }

  const matchedAbbreviation = Object.keys(stateAbbreviations).find(
    (abbreviation) =>
      String(stateAbbreviations[abbreviation]).toUpperCase() === normalizedState
  )

  return matchedAbbreviation ?? normalizedState
}

/**
 * Whether a location change moves the search to a different state.
 * A missing previous state is not a change. A previous state that is not a
 * single value (e.g. a repeated query param) is treated as a change.
 */
export const hasStateChanged = (
  previousState: unknown,
  nextState: unknown
): boolean => {
  if (!previousState) {
    return false
  }

  if (typeof previousState !== 'string' || typeof nextState !== 'string') {
    return true
  }

  return getStateAbbreviation(previousState) !== getStateAbbreviation(nextState)
}
